import { Link } from 'react-router-dom';

interface MarketingButtonProps {
  variant?: 'primary' | 'secondary' | 'outline' | 'dark';
  children: React.ReactNode;
  icon?: React.ComponentType<{ className?: string; 'aria-hidden'?: boolean }>;
  to?: string;
  onClick?: () => void;
  className?: string;
}

/**
 * Marketing button component with multiple variants
 * Supports both Link (React Router) and button modes
 *
 * @example
 * <MarketingButton variant="primary" to="/batch" icon={ArrowRight}>
 *   Try Batch
 * </MarketingButton>
 */
export function MarketingButton({
  variant = 'primary',
  children,
  icon: Icon,
  to,
  onClick,
  className = ''
}: MarketingButtonProps) {
  const baseClasses = "inline-flex items-center gap-2 px-4 py-2.5 rounded-full font-medium transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-[#2F7BFF]/40";

  const variants = {
    primary: "bg-[#2F7BFF] text-white hover:bg-[#2665D9]  shadow-[#2F7BFF]/20",
    secondary: "bg-white text-[#0E1325] border border-[#E6ECF2] hover:border-[#2F7BFF] hover:text-[#2F7BFF] ",
    outline: "bg-transparent text-[#0E1325] border border-[#0E1325] hover:bg-[#0E1325] hover:text-white",
    dark: "bg-white text-[#0B1020] hover:bg-[#F7F9FC] "
  };

  const content = (
    <>
      {children}
      {Icon && <Icon className="w-4 h-4" aria-hidden={true} />}
    </>
  );

  if (to) {
    return (
      <Link
        to={to}
        className={`${baseClasses} ${variants[variant]} ${className}`}
        onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
      >
        {content}
      </Link>
    );
  }

  return (
    <button
      onClick={onClick}
      className={`${baseClasses} ${variants[variant]} ${className}`}
    >
      {content}
    </button>
  );
}
